import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { getApiUrl, getCsrfToken } from "../../utils/apiHelper";
import "../../styles/ProfilesManager.css";
import "../../styles/theme.css";

const getCurrentTheme = () => {
  return document.body.getAttribute("data-theme") || "dark";
};

const WEBSOCKET_INTERFACES = ["websocket", "json_rpc_websocket"];
const HTTP_INTERFACES = ["json_rpc_http", "graphql", "sse"];

const PREFERRED_INTERFACE_OPTIONS = [
  "graphql",
  "json_rpc_http",
  "sse",
  "json_rpc_websocket",
  "websocket",
  "long_poll",
  "http",
  "browser_ui",
  "other",
];

const DEFAULT_ADVANCED_OPTIONS = {
  preferred_interface: "browser_ui",
  request_template_json: "{}",
  response_template_json: "{}",
  method: "",
  browser_mode: "non_interactive",
  google_auth_info_required: false,
  google_auth_info: null,
  login_required: false,
  username: "",
  password: "",
  headers: "",
  prompt_key_path: "",
  response_key_path: "",
  stream_end_markers: "",
};

function isJsonObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function fieldStyle() {
  const light = getCurrentTheme() === "light";
  return {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #374151",
    backgroundColor: light ? "#ffffff" : "#1f2937",
    color: light ? "#111827" : "#ffffff",
    fontSize: "14px",
    width: "100%",
    marginBottom: 0,
    boxSizing: "border-box",
  };
}

const optionBoxStyle = {
  background: "rgba(15, 23, 42, 0.9)",
  borderRadius: "10px",
  border: "1px solid rgba(148, 163, 184, 0.4)",
  padding: "12px 14px",
  color: "#e5e7eb",
  boxSizing: "border-box",
};

function advancedSelectStyle() {
  return {
    ...fieldStyle(),
    padding: "8px 36px 8px 12px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    backgroundSize: "12px",
  };
}

function isInvalidJson(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return false;
  try {
    return !isJsonObject(JSON.parse(trimmed));
  } catch {
    return true;
  }
}

function Field({ label, hint, invalid, children }) {
  return (
    <label className="pm-input-label">
      <div style={{ marginBottom: "8px" }}>
        {label}
        {invalid ? (
          <span
            style={{
              marginLeft: "6px",
              fontSize: "12px",
              fontStyle: "italic",
              fontWeight: 400,
              color: "#ef4444",
            }}
          >
            {invalid}
          </span>
        ) : null}
        {hint ? (
          <span
            style={{
              marginLeft: "6px",
              fontSize: "12px",
              fontStyle: "italic",
              fontWeight: 400,
              color: getCurrentTheme() === "light" ? "#6b7280" : "#9ca3af",
            }}
          >
            {hint}
          </span>
        ) : null}
      </div>
      {children}
    </label>
  );
}

/**
 * Advanced custom-target options.
 * Each preferred interface shows its contract fields.
 * Request/response templates and method are shown for non-browser interfaces.
 */
const CustomTargetAdvanced = forwardRef(function CustomTargetAdvanced(
  { url = "", onChange, advancedSeed = null },
  ref
) {
  const { t } = useTranslation("targets");
  const [advancedOptions, setAdvancedOptions] = useState(() =>
    advancedSeed ? { ...DEFAULT_ADVANCED_OPTIONS, ...advancedSeed } : DEFAULT_ADVANCED_OPTIONS
  );
  const [autoLearnModalOpen, setAutoLearnModalOpen] = useState(false);
  const [appId, setAppId] = useState("");
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [autoLearnUserId, setAutoLearnUserId] = useState("");
  const [autoLearnPassword, setAutoLearnPassword] = useState("");
  const [autoLearnResultOpen, setAutoLearnResultOpen] = useState(false);
  const [autoLearnResultMessage, setAutoLearnResultMessage] = useState("");
  const [autoLearnLoading, setAutoLearnLoading] = useState(false);
  const [qrUploaded, setQrUploaded] = useState(
    () => Boolean(advancedSeed?.google_auth_info_required)
  );
  const [qrAnalyzeLoading, setQrAnalyzeLoading] = useState(false);
  const [qrAnalyzeError, setQrAnalyzeError] = useState("");
  const qrFileRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const clearAuthenticatorQr = () => {
    setQrUploaded(false);
    setQrAnalyzeError("");
    setQrAnalyzeLoading(false);
    if (qrFileRef.current) qrFileRef.current.value = "";
  };

  const handleAuthenticatorQrChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setQrAnalyzeLoading(true);
    setQrAnalyzeError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl("/api/internal/target-config/analyze-authenticator-qr-code/"),
        {
          method: "POST",
          headers: { "X-CSRFToken": csrfToken },
          credentials: "include",
          body: formData,
        }
      );
      const data = await response.json().catch(() => null);
      if (!response.ok || !data || data.error) {
        throw new Error(
          data?.message || t("scriptsParameters.advancedOptions.authenticatorQrAnalyzeFailed")
        );
      }

      setQrUploaded(true);
      setAdvancedOptions((prev) => ({
        ...prev,
        google_auth_info: {
          secret_key: data.secret_key ?? "",
          otpauth_uri: data.otpauth_uri ?? "",
        },
      }));
    } catch (error) {
      console.error("analyze-authenticator-qr-code failed:", error);
      setQrUploaded(false);
      if (qrFileRef.current) qrFileRef.current.value = "";
      setQrAnalyzeError(
        error?.message || t("scriptsParameters.advancedOptions.authenticatorQrAnalyzeFailed")
      );
    } finally {
      setQrAnalyzeLoading(false);
    }
  };

  useEffect(() => {
    onChangeRef.current?.(advancedOptions);
  }, [advancedOptions]);

  const showAutoLearnResult = (message) => {
    setAutoLearnResultMessage(message);
    setAutoLearnResultOpen(true);
  };

  const closeAutoLearnResult = () => {
    setAutoLearnResultOpen(false);
    setAutoLearnResultMessage("");
  };

  const resetAutoLearnForm = () => {
    setAppId("");
    setRequiresAuth(false);
    setAutoLearnUserId("");
    setAutoLearnPassword("");
  };

  const closeAutoLearnModal = () => {
    setAutoLearnModalOpen(false);
    resetAutoLearnForm();
  };

  const finishAutoLearn = (message) => {
    setAutoLearnLoading(false);
    showAutoLearnResult(message);
  };

  const handleAutoLearn = async () => {
    if (autoLearnLoading) return;

    if (!url.trim()) {
      closeAutoLearnModal();
      showAutoLearnResult(t("scriptsParameters.enterTargetUrl"));
      return;
    }

    const payload = {
      target_url: url,
      app_id: appId,
      require_login: requiresAuth,
      ...(requiresAuth
        ? { userid: autoLearnUserId, passwd: autoLearnPassword }
        : {}),
    };

    closeAutoLearnModal();
    setAutoLearnLoading(true);
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl("/api/internal/skyvern/schema-probe/"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success || !data.preferred_interface) {
        finishAutoLearn(t("scriptsParameters.autoLearnFailed"));
        return;
      }

      const preferredInterface = data.preferred_interface;

      setAdvancedOptions({
        ...DEFAULT_ADVANCED_OPTIONS,
        preferred_interface: preferredInterface,
      });
      finishAutoLearn(t("scriptsParameters.autoLearnSuccess"));
    } catch (error) {
      console.error("schema-probe failed:", error);
      finishAutoLearn(t("scriptsParameters.autoLearnFailed"));
    }
  };

  const setField = (key) => (event) => {
    const value = event.target.value;
    setAdvancedOptions((prev) => ({ ...prev, [key]: value }));
  };

  useImperativeHandle(ref, () => ({
    reset: () => {
      setAdvancedOptions(DEFAULT_ADVANCED_OPTIONS);
      setAutoLearnModalOpen(false);
      setAutoLearnLoading(false);
      setAutoLearnResultOpen(false);
      setAutoLearnResultMessage("");
      resetAutoLearnForm();
      clearAuthenticatorQr();
    },
  }));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <p
          style={{
            margin: 0,
            flex: "1 1 240px",
            color: getCurrentTheme() === "light" ? "#6b7280" : "#9ca3af",
            fontSize: "12px",
            fontStyle: "italic",
            lineHeight: 1.5,
          }}
        >
          {t("scriptsParameters.advancedOptions.intro")}
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setAutoLearnModalOpen(true)}
          style={{ flex: "0 0 auto" }}
        >
          {t("scriptsParameters.autoLearn")}
        </button>
      </div>
      <Field label={t("scriptsParameters.advancedOptions.preferredInterface")}>
        <select
          value={advancedOptions.preferred_interface}
          onChange={(event) =>
            setAdvancedOptions((prev) => ({
              ...prev,
              preferred_interface: event.target.value,
            }))
          }
          style={advancedSelectStyle()}
        >
          {(PREFERRED_INTERFACE_OPTIONS.includes(advancedOptions.preferred_interface)
            ? PREFERRED_INTERFACE_OPTIONS
            : [advancedOptions.preferred_interface, ...PREFERRED_INTERFACE_OPTIONS]
          ).map((value) => (
            <option key={value} value={value}>
              {t(`scriptsParameters.advancedOptions.transports.${value}`, {
                defaultValue: value,
              })}
            </option>
          ))}
        </select>
      </Field>

      {advancedOptions.preferred_interface === "browser_ui" && (
        <div
          style={{
            ...optionBoxStyle,
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          {[
            { value: "non_interactive", labelKey: "nonInteractiveBrowser" },
            { value: "interactive", labelKey: "interactiveBrowser" },
          ].map(({ value, labelKey }) => (
            <label
              key={value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                fontSize: "14px",
                color: "#e5e7eb",
              }}
            >
              <input
                type="radio"
                name="browser_mode"
                value={value}
                checked={
                  (advancedOptions.browser_mode || "non_interactive") === value
                }
                onChange={() =>
                  setAdvancedOptions((prev) => ({
                    ...prev,
                    browser_mode: value,
                  }))
                }
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  accentColor: "#2563eb",
                }}
              />
              {t(`scriptsParameters.advancedOptions.${labelKey}`)}
            </label>
          ))}
        </div>
      )}

      {advancedOptions.preferred_interface === "browser_ui" && (
        <div>
          <label className="pm-input-label">
            <div
              style={{
                marginBottom: advancedOptions.google_auth_info_required ? "8px" : 0,
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {t("scriptsParameters.advancedOptions.googleAuthenticatorRequired")}
              <input
                type="checkbox"
                checked={Boolean(advancedOptions.google_auth_info_required)}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setAdvancedOptions((prev) => ({
                    ...prev,
                    google_auth_info_required: checked,
                    google_auth_info: checked ? prev.google_auth_info : null,
                  }));
                  if (!checked) clearAuthenticatorQr();
                }}
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  accentColor: "#2563eb",
                }}
              />
            </div>
          </label>
          {advancedOptions.google_auth_info_required ? (
            <div
              style={{
                ...optionBoxStyle,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1, minWidth: "160px" }}>
                <div style={{ fontWeight: 600, marginBottom: "2px" }}>
                  {t("scriptsParameters.advancedOptions.secretJsonKey")}
                </div>
                <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                  {t("scriptsParameters.advancedOptions.authenticatorQrDesc")}
                </div>
                {qrAnalyzeError ? (
                  <div style={{ marginTop: "6px", fontSize: "12px", color: "#ef4444" }}>
                    {qrAnalyzeError}
                  </div>
                ) : null}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {qrAnalyzeLoading ? (
                  <i
                    className="fas fa-spinner fa-spin"
                    aria-hidden="true"
                    style={{ color: "#93c5fd", fontSize: "14px" }}
                  />
                ) : qrUploaded ? (
                  <i
                    className="fas fa-check"
                    aria-hidden="true"
                    style={{ color: "#34d399", fontSize: "14px" }}
                  />
                ) : null}
                <input
                  ref={qrFileRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  style={{ display: "none" }}
                  onChange={handleAuthenticatorQrChange}
                />
                <button
                  type="button"
                  onClick={() => qrFileRef.current?.click()}
                  disabled={qrAnalyzeLoading}
                  style={{
                    background: "rgba(37, 99, 235, 0.15)",
                    border: "1px solid rgba(96, 165, 250, 0.45)",
                    borderRadius: "8px",
                    color: "#93c5fd",
                    cursor: qrAnalyzeLoading ? "wait" : "pointer",
                    padding: "6px 12px",
                    fontSize: "13px",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    opacity: qrAnalyzeLoading ? 0.7 : 1,
                  }}
                >
                  <i className="fas fa-file-upload" aria-hidden="true" />
                  {t("scriptsParameters.chooseFile")}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {advancedOptions.preferred_interface === "browser_ui" && (
        <div>
          <label className="pm-input-label">
            <div
              style={{
                marginBottom: advancedOptions.login_required ? "8px" : 0,
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {t("scriptsParameters.advancedOptions.loginRequired")}
              <input
                type="checkbox"
                checked={Boolean(advancedOptions.login_required)}
                onChange={(event) =>
                  setAdvancedOptions((prev) => ({
                    ...prev,
                    login_required: event.target.checked,
                  }))
                }
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  accentColor: "#2563eb",
                }}
              />
            </div>
          </label>
          {advancedOptions.login_required ? (
            <div
              style={{
                ...optionBoxStyle,
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <label className="pm-input-label" style={{ marginBottom: 0, color: "inherit" }}>
                {t("scriptsParameters.advancedOptions.username")}
                <input
                  type="text"
                  className="pm-input"
                  value={advancedOptions.username}
                  onChange={setField("username")}
                  autoComplete="off"
                />
              </label>
              <label className="pm-input-label" style={{ marginBottom: 0, color: "inherit" }}>
                {t("scriptsParameters.password")}
                <input
                  type="password"
                  className="pm-input"
                  value={advancedOptions.password}
                  onChange={setField("password")}
                  autoComplete="new-password"
                />
              </label>
            </div>
          ) : null}
        </div>
      )}

      {advancedOptions.preferred_interface !== "browser_ui" && (
        <>
          <Field
            label={t("scriptsParameters.advancedOptions.requestTemplate")}
            invalid={
              isInvalidJson(advancedOptions.request_template_json)
                ? t("scriptsParameters.advancedOptions.invalidJson")
                : ""
            }
          >
            <textarea
              className="pm-input"
              rows="6"
              value={advancedOptions.request_template_json}
              onChange={setField("request_template_json")}
            />
          </Field>
          <Field
            label={t("scriptsParameters.advancedOptions.responseTemplate")}
            invalid={
              isInvalidJson(advancedOptions.response_template_json)
                ? t("scriptsParameters.advancedOptions.invalidJson")
                : ""
            }
          >
            <textarea
              className="pm-input"
              rows="6"
              value={advancedOptions.response_template_json}
              onChange={setField("response_template_json")}
            />
          </Field>
        </>
      )}

      {HTTP_INTERFACES.includes(advancedOptions.preferred_interface) && (
        <Field label={t("scriptsParameters.advancedOptions.method")}>
          <input
            type="text"
            className="pm-input"
            value={advancedOptions.method}
            onChange={setField("method")}
          />
        </Field>
      )}

      {(WEBSOCKET_INTERFACES.includes(advancedOptions.preferred_interface) ||
        HTTP_INTERFACES.includes(advancedOptions.preferred_interface)) && (
        <>
          <Field label={t("scriptsParameters.advancedOptions.headers")}>
            <textarea
              className="pm-input"
              rows="4"
              value={advancedOptions.headers}
              onChange={setField("headers")}
            />
          </Field>
          <Field
            label={t("scriptsParameters.advancedOptions.promptKeyPath")}
            hint={t("scriptsParameters.advancedOptions.commaSeparated")}
          >
            <input
              type="text"
              className="pm-input"
              value={advancedOptions.prompt_key_path}
              onChange={setField("prompt_key_path")}
            />
          </Field>
          <Field
            label={t("scriptsParameters.advancedOptions.responseKeyPath")}
            hint={t("scriptsParameters.advancedOptions.commaSeparated")}
          >
            <input
              type="text"
              className="pm-input"
              value={advancedOptions.response_key_path}
              onChange={setField("response_key_path")}
            />
          </Field>
          {advancedOptions.preferred_interface === "sse" && (
            <Field
              label={t("scriptsParameters.advancedOptions.streamEndMarkers")}
              hint={t("scriptsParameters.advancedOptions.commaSeparated")}
            >
              <input
                type="text"
                className="pm-input"
                value={advancedOptions.stream_end_markers}
                onChange={setField("stream_end_markers")}
              />
            </Field>
          )}
        </>
      )}

      {autoLearnModalOpen && (
        <div className="modal-backdrop" onClick={closeAutoLearnModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "fadeIn 0.3s ease-out" }}
          >
            <h3 style={{ textAlign: "center" }}>{t("scriptsParameters.autoLearn")}</h3>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <label className="pm-input-label">
                {t("scriptsParameters.appId")}
                <input
                  type="text"
                  className="pm-input"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                />
              </label>
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 600,
                color: getCurrentTheme() === "light" ? "#111827" : "#e5e7eb",
                marginBottom: "16px",
              }}
            >
              <input
                type="checkbox"
                checked={requiresAuth}
                onChange={(e) => setRequiresAuth(e.target.checked)}
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  accentColor: "#2563eb",
                }}
              />
              {t("scriptsParameters.requiresAuth")}
            </label>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <label
                className="pm-input-label"
                style={{
                  color: requiresAuth ? undefined : "#9ca3af",
                  opacity: requiresAuth ? 1 : 0.6,
                }}
              >
                {t("scriptsParameters.userId")}
                <input
                  type="text"
                  className="pm-input"
                  value={autoLearnUserId}
                  onChange={(e) => setAutoLearnUserId(e.target.value)}
                  disabled={!requiresAuth}
                  style={{
                    opacity: requiresAuth ? 1 : 0.5,
                    cursor: requiresAuth ? "text" : "not-allowed",
                  }}
                />
              </label>
              <label
                className="pm-input-label"
                style={{
                  color: requiresAuth ? undefined : "#9ca3af",
                  opacity: requiresAuth ? 1 : 0.6,
                }}
              >
                {t("scriptsParameters.password")}
                <input
                  type="password"
                  className="pm-input"
                  value={autoLearnPassword}
                  onChange={(e) => setAutoLearnPassword(e.target.value)}
                  disabled={!requiresAuth}
                  style={{
                    opacity: requiresAuth ? 1 : 0.5,
                    cursor: requiresAuth ? "text" : "not-allowed",
                  }}
                />
              </label>
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                flexWrap: "wrap",
                marginTop: "20px",
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeAutoLearnModal}
              >
                {t("scriptsParameters.cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAutoLearn}
              >
                {t("scriptsParameters.continue")}
              </button>
            </div>
          </div>
        </div>
      )}

      {autoLearnLoading && (
        <div className="blocking-overlay">
          <div className="blocking-box">
            <div className="blocking-spinner" />
            <div className="blocking-text">{t("scriptsParameters.autoLearning")}</div>
          </div>
        </div>
      )}

      {autoLearnResultOpen && (
        <div className="modal-backdrop" onClick={closeAutoLearnResult}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "fadeIn 0.15s ease-out" }}
          >
            <p
              style={{
                margin: "0 0 24px",
                color: "var(--muted-text)",
                fontSize: "14px",
                lineHeight: 1.5,
                textAlign: "center",
              }}
            >
              {autoLearnResultMessage}
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={closeAutoLearnResult}
              >
                {t("scriptsParameters.ok")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default CustomTargetAdvanced;
